import type { NavItemDataProps } from "@/components/nav";
import userManageService from "@/api/services/userManageService";
import { useFilteredBreadcrumbData } from "@/layouts/dashboard/nav";
import useLocale from "@/locales/use-locale";
import {
	Breadcrumb,
	BreadcrumbEllipsis,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/ui/breadcrumb";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useMatches } from "react-router";

const MUTATION_ROUTE = ["create", ":id"];

interface BreadCrumbProps {
	maxItems?: number;
}

type NavItem = Pick<NavItemDataProps, "hidden" | "path" | "title"> & {
	children?: NavItem[];
};

interface BreadcrumbItemData {
	key: string;
	label: string;
	items: Array<{
		key: string;
		label: string;
		hidden: boolean | undefined;
	}>;
}

export const resolveBreadcrumbPath = (
	path: string,
	params: Record<string, string | undefined>,
) =>
	path.replace(/:([A-Za-z0-9_]+)/g, (segment, paramName: string) => {
		const value = params[paramName];
		return value ? encodeURIComponent(value) : segment;
	});

export default function BreadCrumb({ maxItems = 3 }: BreadCrumbProps) {
	const { t } = useLocale();
	const matches = useMatches();
	const location = useLocation();
	const navData = useFilteredBreadcrumbData();
	const routeParams = Object.fromEntries(
		matches.flatMap((match) => Object.entries(match.params ?? {})),
	);
	const resolveItemPath = (path: string) =>
		resolveBreadcrumbPath(path, routeParams);
	const [detailLabel, setDetailLabel] = useState<string>();
	const getOperationDetailLabel = useCallback(
		(path: string) => {
			const match = path.match(/^\/user-manage\/operation\/([^/?#]+)$/);
			if (!match) {
				return undefined;
			}
			if (MUTATION_ROUTE.includes(match[1])) {
				return undefined;
			}
			return detailLabel ?? "";
		},
		[detailLabel],
	);

	const findPathInNavData = useCallback(
		(path: string, items: NavItem[]): NavItem[] => {
			for (const item of items) {
				const toIdRoute = (path: string) =>
					path.replace(/(\/)\d+(?=\/?$|[?#])/, "$1:id");
				if (item.path === path) {
					return [item];
				}
				if (item.path === toIdRoute(path)) {
					return [item];
				}
				if (item.children) {
					const found = findPathInNavData(path, item.children);
					if (found.length > 0) {
						return [item, ...found];
					}
				}
			}
			return [];
		},
		[],
	);

	// 活动管理：?mode=view 时把「编辑活动」改为「查看活动」
	const isActivityViewMode =
		/^\/operate-center\/activity-manage\/edit\/\d+/.test(location.pathname) &&
		new URLSearchParams(location.search).get("mode") === "view";
	const schoolWikiEditLabel =
		location.pathname === "/operate-center/school-wiki/edit"
			? new URLSearchParams(location.search).get("id")
				? "编辑学校"
				: "新建学校"
			: undefined;

	const breadCrumbs = useMemo(() => {
		const paths = matches
			.filter((item) => item.pathname !== "/")
			.map((item) => item.pathname);

		return paths
			.map((path) => {
				const navItems = navData.flatMap((section) => section.items);
				const pathItems = findPathInNavData(path, navItems);

				if (pathItems.length === 0) return null;

				const currentItem = pathItems[pathItems.length - 1];
				const children =
					currentItem.children?.map((child) => ({
						key: child.path,
						label: t(child.title),
						hidden: child.hidden,
					})) ?? [];

				const isCurrent = path === location.pathname;
				let label: string = t(currentItem.title);
				if (isCurrent && getOperationDetailLabel(path) !== undefined) {
					label = getOperationDetailLabel(path) ?? "";
				} else if (isCurrent && isActivityViewMode) {
					label = "查看活动";
				} else if (isCurrent && schoolWikiEditLabel) {
					label = schoolWikiEditLabel;
				}

				return {
					key: currentItem.path,
					label,
					items: children,
				};
			})
			.filter((item): item is BreadcrumbItemData => item !== null);
	}, [
		findPathInNavData,
		getOperationDetailLabel,
		isActivityViewMode,
		location.pathname,
		matches,
		navData,
		schoolWikiEditLabel,
		t,
	]);

	useEffect(() => {
		const match = location.pathname.match(
			/^\/user-manage\/operation\/([^/?#]+)/,
		);
		if (!match?.[1]) {
			setDetailLabel(undefined);
			return;
		}

		const targetId = match[1];
		// "create" 等静态子路由不是用户 id，不要拿去查用户详情
		if (MUTATION_ROUTE.includes(targetId)) {
			setDetailLabel(undefined);
			return;
		}

		let cancelled = false;

		void (async () => {
			try {
				const user = await userManageService.getEnterpriseUserDetail(targetId);
				if (cancelled) return;
				setDetailLabel(
					user?.personal?.operator === "0" ? "个人用户详情" : "运营号详情",
				);
			} catch {
				if (cancelled) return;
				setDetailLabel(undefined);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [location.pathname]);

	const renderBreadcrumbItem = (item: BreadcrumbItemData, isLast: boolean) => {
		const switchableItems = item.items.filter(
			(i) =>
				!i.hidden && !MUTATION_ROUTE.includes(i.key.split("/").slice(-1)[0]),
		);
		const hasItems = switchableItems.length > 0;

		if (hasItems) {
			return (
				<BreadcrumbItem>
					<DropdownMenu>
						<DropdownMenuTrigger className="flex items-center gap-1">
							{item.label}
							<ChevronDown className="h-4 w-4" />
						</DropdownMenuTrigger>
						<DropdownMenuContent align="start">
							{switchableItems.map((subItem) => (
								<DropdownMenuItem key={subItem.key} asChild>
									<Link
										to={resolveItemPath(subItem.key)}
										className="text-gray-800"
									>
										{subItem.label}
									</Link>
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</BreadcrumbItem>
			);
		}

		return (
			<BreadcrumbItem>
				{isLast ? (
					<BreadcrumbPage>{item.label}</BreadcrumbPage>
				) : (
					<BreadcrumbLink asChild>
						<Link to={resolveItemPath(item.key)}>{item.label}</Link>
					</BreadcrumbLink>
				)}
			</BreadcrumbItem>
		);
	};

	const renderBreadcrumbs = () => {
		if (breadCrumbs.length <= maxItems) {
			return breadCrumbs.map((item, index) => (
				<React.Fragment key={item.key}>
					{renderBreadcrumbItem(item, index === breadCrumbs.length - 1)}
					{index < breadCrumbs.length - 1 && <BreadcrumbSeparator />}
				</React.Fragment>
			));
		}

		// Show first item, ellipsis, and last maxItems-1 items
		const firstItem = breadCrumbs[0];
		const lastItems = breadCrumbs.slice(-(maxItems - 1));
		const hiddenItems = breadCrumbs.slice(1, -(maxItems - 1));

		return (
			<>
				{renderBreadcrumbItem(firstItem, false)}
				<BreadcrumbSeparator />
				<BreadcrumbItem>
					<DropdownMenu>
						<DropdownMenuTrigger className="flex items-center gap-1">
							<BreadcrumbEllipsis />
						</DropdownMenuTrigger>
						<DropdownMenuContent align="start">
							{hiddenItems.map((item) => (
								<DropdownMenuItem key={item.key} asChild>
									<Link to={resolveItemPath(item.key)}>{item.label}</Link>
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				</BreadcrumbItem>
				<BreadcrumbSeparator />
				{lastItems.map((item, index) => (
					<React.Fragment key={item.key}>
						{renderBreadcrumbItem(item, index === lastItems.length - 1)}
						{index < lastItems.length - 1 && <BreadcrumbSeparator />}
					</React.Fragment>
				))}
			</>
		);
	};

	return (
		<Breadcrumb>
			<BreadcrumbList>{renderBreadcrumbs()}</BreadcrumbList>
		</Breadcrumb>
	);
}
